/**
 * API Client Configuration
 * Centralized Axios instance with interceptors for authentication, error handling,
 * and request/response transformation
 *
 * Updated to use Microsoft Entra External ID (MSAL) for authentication.
 * Includes correlation ID support for end-to-end request tracing.
 */

import axios, { AxiosInstance, AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import axiosRetry, { isNetworkOrIdempotentRequestError, exponentialDelay } from 'axios-retry';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { ApiResponse, ApiError } from '@creatoros/shared';
import { isRetryableHttpStatus, isNetworkError } from '@creatoros/shared';
import { env } from '../config/env';
import { getMsalInstance, tokenRequest, loginRequest } from '../config/authConfig';
import {
  isAuthRedirectInProgress,
  markAuthRedirectInProgress,
  isWithinRedirectCooldown,
} from '../utils/authRedirect';
import { analyticsService } from './analytics.service';
import { logger } from '../utils/logger';
import { getClientLogLevelHeader, LOG_LEVEL_HEADER } from '../utils/log-level';
import { payloadEncryption } from '../utils/encryption.util';
import { getTokenExpiresOnCache } from '../utils/tokenCache';

/**
 * Get correlation ID header name
 */
const CORRELATION_HEADER = 'X-Correlation-ID';

/**
 * Impersonation session header name
 */
const IMPERSONATION_HEADER = 'X-Impersonation-Session';

/**
 * Payload encryption header name
 */
const ENCRYPTION_HEADER = 'X-Encrypted';

/**
 * Session storage key for impersonation session ID
 */
const IMPERSONATION_SESSION_KEY = 'impersonation_session_id';

function getSafeSameOriginPath(value: unknown, fallback: string): string {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) {
    return fallback;
  }

  try {
    const parsed = new URL(value, window.location.origin);
    if (parsed.origin !== window.location.origin) {
      return fallback;
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return fallback;
  }
}

/**
 * Get the current impersonation session ID from session storage
 */
export function getImpersonationSessionId(): string | null {
  return sessionStorage.getItem(IMPERSONATION_SESSION_KEY);
}

/**
 * Set the impersonation session ID in session storage
 */
export function setImpersonationSessionId(sessionId: string | null): void {
  if (sessionId) {
    sessionStorage.setItem(IMPERSONATION_SESSION_KEY, sessionId);
  } else {
    sessionStorage.removeItem(IMPERSONATION_SESSION_KEY);
  }
}

/**
 * Clear the impersonation session
 */
export function clearImpersonationSession(): void {
  sessionStorage.removeItem(IMPERSONATION_SESSION_KEY);
}

/**
 * Get or generate a correlation ID for API requests
 *
 * Uses the Application Insights operation ID if available to ensure
 * the same correlation ID flows through frontend telemetry and backend logs.
 * Falls back to generating a UUID if App Insights is not initialized.
 *
 * This enables support teams to search by a single ID to find:
 * - Frontend App Insights telemetry (traces, exceptions, events)
 * - Backend logs and telemetry (via X-Correlation-ID header)
 */
function getCorrelationId(): string {
  // Try to get the operation ID from App Insights for unified tracing
  const operationId = analyticsService.getOperationId();

  // The analyticsService always returns an ID (generates one if App Insights not available)
  return operationId;
}

/**
 * Custom error class for API errors
 */
export class ApiClientError extends Error {
  constructor(
    public code: string,
    message: string,
    public details?: Record<string, unknown>,
    public status?: number
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

/**
 * MSAL Token Acquisition
 *
 * Acquires access tokens from Entra External ID using MSAL.
 * Handles silent token acquisition and interactive fallback.
 */
export const msalTokenAcquisition = {
  /**
   * Get access token for API calls
   *
   * Attempts silent token acquisition first. If interaction is required,
   * initiates a redirect to Entra External ID.
   * Includes retry logic for transient network errors.
   *
   * @param forceRefresh - If true, forces MSAL to fetch a new token from the server
   *                       instead of using the cached token. Useful for proactive
   *                       token refresh before expiration.
   */
  getAccessToken: async (forceRefresh: boolean = false): Promise<string | null> => {
    const maxRetries = 2; // Retry once for transient errors

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const msalInstance = getMsalInstance();
        const accounts = msalInstance.getAllAccounts();

        if (accounts.length === 0) {
          // No authenticated account
          logger.warn('No authenticated account found');
          return null;
        }

        const account = accounts[0];

        try {
          // Attempt silent token acquisition
          const response = await msalInstance.acquireTokenSilent({
            ...tokenRequest,
            account: account,
            forceRefresh: forceRefresh, // Allow forcing refresh from server
          });

          return response.accessToken;
        } catch (error) {
          if (error instanceof InteractionRequiredAuthError) {
            // Don't trigger login redirect while on the auth callback page.
            // The callback page is still processing the incoming auth code via
            // handleRedirectPromise(). Redirecting away would abort that flow
            // and cause a redirect loop.
            if (window.location.pathname === '/auth/callback') {
              logger.warn('Interaction required but on auth callback page, skipping redirect');
              return null;
            }

            // Don't trigger login redirect during cooldown period after a redirect
            // just completed. This prevents dashboard components from triggering
            // immediate re-redirect before tokens are fully propagated.
            if (isWithinRedirectCooldown()) {
              logger.warn('Interaction required but within redirect cooldown, skipping redirect');
              return null;
            }

            // Interaction required - need to redirect to login
            // Don't retry this - user interaction is needed
            logger.warn('Interaction required, redirecting to login');

            if (isAuthRedirectInProgress()) {
              logger.warn('Auth redirect already in progress, skipping duplicate redirect');
              return null;
            }

            markAuthRedirectInProgress();

            // Save the current path for redirect after login
            sessionStorage.setItem('auth_redirect_uri', window.location.pathname);

            // Null out activeAccount before redirect so MSAL does not
            // auto-inject the OID-based username as login_hint
            msalInstance.setActiveAccount(null);

            // Redirect to login without a hint — avoids confusing OID-based email
            await msalInstance.loginRedirect({
              ...loginRequest,
            });

            // This code won't execute as user will be redirected
            return null;
          }

          // For other errors, check if we should retry
          if (attempt >= maxRetries) {
            throw error;
          }

          // Retry on transient errors (network issues, temporary MSAL issues)
          logger.warn(
            `Token acquisition failed (Attempt ${attempt + 1}/${maxRetries + 1}), retrying...`,
            {
              error: error instanceof Error ? error.message : String(error),
            }
          );

          // Wait before retrying: 500ms, 1000ms
          const delayMs = 500 * Math.pow(2, attempt);
          await new Promise((resolve) => setTimeout(resolve, delayMs));

          // Continue to next retry attempt
          continue;
        }
      } catch (error) {
        if (attempt >= maxRetries) {
          logger.error('Token acquisition failed after retries', error);
          return null;
        }
      }
    }

    return null;
  },

  /**
   * Clear authentication (logout)
   */
  clearAuth: async (): Promise<void> => {
    try {
      const msalInstance = getMsalInstance();
      const accounts = msalInstance.getAllAccounts();

      if (accounts.length > 0) {
        await msalInstance.logoutRedirect({
          account: accounts[0],
        });
      }
    } catch (error) {
      logger.error('Logout failed', error);
    }
  },
};

/**
 * Legacy token storage utilities
 * @deprecated Use msalTokenAcquisition instead
 */
export const tokenStorage = {
  getAccessToken: (): string | null => {
    logger.warn('tokenStorage.getAccessToken is deprecated, using MSAL');
    return null;
  },

  setAccessToken: (_token: string): void => {
    logger.warn('tokenStorage.setAccessToken is deprecated, MSAL handles tokens');
  },

  getRefreshToken: (): string | null => {
    logger.warn('tokenStorage.getRefreshToken is deprecated, MSAL handles refresh');
    return null;
  },

  setRefreshToken: (_token: string): void => {
    logger.warn('tokenStorage.setRefreshToken is deprecated, MSAL handles tokens');
  },

  clearTokens: (): void => {
    logger.warn('tokenStorage.clearTokens is deprecated, use msalTokenAcquisition.clearAuth');
  },
};

/**
 * Create configured Axios instance
 */
const createApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: env.apiUrl,
    timeout: env.apiTimeout,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  /**
   * Configure axios-retry for automatic retries with exponential backoff
   */
  axiosRetry(client, {
    retries: 3, // Maximum retry attempts
    retryDelay: (retryCount, error) => {
      // Use exponential delay: 1s, 2s, 4s
      const delay = exponentialDelay(retryCount, error);

      // Track retry attempt in analytics
      try {
        const correlationId = (error.config as any)?.correlationId;
        analyticsService.trackEvent('API_Retry', {
          url: error.config?.url,
          method: error.config?.method,
          attempt: retryCount,
          delayMs: delay,
          errorType: error.code || 'UnknownError',
          correlationId,
        });
      } catch (trackingError) {
        logger.warn('Failed to track retry', {
          error: trackingError instanceof Error ? trackingError.message : String(trackingError),
        });
      }

      return delay;
    },
    retryCondition: (error) => {
      // Don't retry if we already have a _retry flag (from 401 handler)

      if ((error.config as any)?._retry) {
        return false;
      }

      // Don't retry client errors (except specific ones)
      if (error.response?.status && error.response.status >= 400 && error.response.status < 500) {
        // Only retry specific 4xx errors
        const retryable4xx = [408, 429]; // Timeout, Rate Limit
        if (!retryable4xx.includes(error.response.status)) {
          return false;
        }
      }

      // Retry network errors or idempotent requests
      if (isNetworkOrIdempotentRequestError(error)) {
        return true;
      }

      // Retry on specific status codes (5xx, 408, 429)
      if (error.response?.status) {
        return isRetryableHttpStatus(error.response.status);
      }

      // Retry on network errors
      return isNetworkError(error);
    },
    onRetry: (retryCount, _error, requestConfig) => {
      logger.debug(
        `Retrying request: ${requestConfig.method?.toUpperCase()} ${requestConfig.url} (Attempt ${retryCount}/3)`
      );
    },
  });

  /**
   * Request interceptor
   * - Gets correlation ID from App Insights for unified tracing
   * - Acquires authentication token from MSAL
   * - Adds token to request headers
   * - Logs requests in debug mode
   */
  client.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      // Get correlation ID from App Insights for end-to-end tracing
      // This ensures the same ID is used in frontend telemetry and backend logs
      if (config.headers) {
        const correlationId = getCorrelationId();
        config.headers[CORRELATION_HEADER] = correlationId;

        const logLevelHeader = getClientLogLevelHeader();
        if (logLevelHeader) {
          config.headers[LOG_LEVEL_HEADER] = logLevelHeader;
        }

        // Store correlation ID in config for logging
        (config as any).correlationId = correlationId;

        logger.trace('[api.client] Headers injected', {
          event: 'http.headers.injected',
          correlationId,
          hasLogLevelHeader: !!logLevelHeader,
        });

        logger.debug('[api.client] Request', {
          event: 'http.request',
          method: config.method?.toUpperCase(),
          url: config.url,
          correlationId,
        });
      }

      // Acquire access token from MSAL.
      // Pre-flight synchronous expiry check using the tokenExpiresOn timestamp
      // maintained by useTokenRefresh after each successful acquireTokenSilent.
      // If we already know the token is expired, skip the network request entirely
      // and trigger login redirect immediately — saving one HTTP round-trip and
      // avoiding a visible 401 in the network tab.
      try {
        const tokenExpiresOn = getTokenExpiresOnCache();
        if (tokenExpiresOn !== null && Date.now() >= tokenExpiresOn) {
          logger.warn(
            '[api.client] Token expired (pre-flight check), skipping request and redirecting'
          );
          if (!isAuthRedirectInProgress() && window.location.pathname !== '/auth/callback') {
            markAuthRedirectInProgress();
            sessionStorage.setItem('auth_redirect_uri', window.location.pathname);
            const msalInstance = getMsalInstance();
            msalInstance.setActiveAccount(null);
            await msalInstance.loginRedirect({ ...loginRequest });
          }
          return Promise.reject(new ApiClientError('TOKEN_EXPIRED', 'Access token has expired'));
        }

        if (isAuthRedirectInProgress()) {
          return Promise.reject(
            new ApiClientError('AUTH_IN_PROGRESS', 'Authentication in progress')
          );
        }

        // Microsoft best practice: always acquire a valid token before any API call.
        // acquireTokenSilent respects tokenRenewalOffsetSeconds (300s) and will
        // proactively refresh the token before it actually expires.
        // If token acquisition returns null or throws, we MUST block the request —
        // never send a call to the backend without a valid Bearer token.
        const token = await msalTokenAcquisition.getAccessToken();
        if (!token) {
          logger.warn('[api.client] Unable to acquire access token — blocking request');
          return Promise.reject(
            new ApiClientError(
              'NO_TOKEN',
              'Unable to acquire a valid access token. Please sign in.'
            )
          );
        }
        if (config.headers) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        // Re-throw ApiClientErrors as-is (already handled above)
        if (error instanceof ApiClientError) {
          return Promise.reject(error);
        }
        logger.error('[api.client] Token acquisition threw unexpectedly — blocking request', error);
        // Block the request: sending without a token would expose the call to
        // rejection by the backend and could mask the real auth failure.
        return Promise.reject(
          new ApiClientError(
            'TOKEN_ACQUISITION_FAILED',
            'Failed to acquire access token before making the request.'
          )
        );
      }

      // Add impersonation session header if active
      const impersonationSessionId = getImpersonationSessionId();
      if (impersonationSessionId && config.headers) {
        config.headers[IMPERSONATION_HEADER] = impersonationSessionId;
      }

      // Encrypt request body if payload encryption is enabled
      if (payloadEncryption.isEnabled() && config.data && config.headers) {
        try {
          config.data = await payloadEncryption.encrypt(config.data);
          config.headers[ENCRYPTION_HEADER] = 'true';
        } catch (encryptError) {
          logger.error('Failed to encrypt request payload', encryptError);
          // Continue with unencrypted payload as fallback
        }
      }

      return config;
    },
    (error: AxiosError) => {
      logger.error('API request error', error);
      return Promise.reject(error);
    }
  );

  /**
   * Response interceptor
   * - Extracts correlation ID from response for logging
   * - Transforms API responses
   * - Handles errors and token refresh
   * - Logs responses in debug mode
   */
  client.interceptors.response.use(
    async (response: AxiosResponse<ApiResponse>) => {
      const correlationId =
        (response.config as any)?.correlationId ||
        response.headers[CORRELATION_HEADER.toLowerCase()] ||
        (response.data as any)?.metadata?.correlationId;

      logger.debug('[api.client] Response', {
        event: 'http.response',
        method: response.config.method?.toUpperCase(),
        url: response.config.url,
        status: response.status,
        correlationId,
      });

      // Decrypt response if encrypted
      if (
        payloadEncryption.isEnabled() &&
        response.headers[ENCRYPTION_HEADER.toLowerCase()] === 'true' &&
        response.data &&
        payloadEncryption.isEncryptedPayload(response.data)
      ) {
        try {
          response.data = (await payloadEncryption.decrypt(
            response.data
          )) as unknown as ApiResponse;
        } catch (decryptError) {
          logger.error('Failed to decrypt response payload', decryptError);
          throw new ApiClientError('DECRYPTION_ERROR', 'Failed to decrypt response');
        }
      }

      // Return the data property from ApiResponse
      return response;
    },
    async (error: AxiosError<ApiResponse>) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & {
        _retry?: boolean;
      };

      const correlationId =
        (originalRequest as any)?.correlationId ||
        error.response?.headers[CORRELATION_HEADER.toLowerCase()] ||
        (error.response?.data as any)?.metadata?.correlationId;

      // Track API error in analytics
      try {
        const responseData = error.response?.data as any;
        const errorMessage = responseData?.error || error.message || 'API request failed';
        const errorCode = responseData?.errorType || 'UNKNOWN_ERROR';

        logger.error('API response error', error, {
          event: 'http.request.failed',
          status: error.response?.status,
          url: error.config?.url,
          method: error.config?.method,
          errorCode,
          correlationId,
        });

        analyticsService.trackException(
          new Error(`API Error: ${errorMessage}`),
          {
            url: error.config?.url,
            method: error.config?.method,
            status: error.response?.status,
            errorCode,
            correlationId,
          },
          error.response?.status ? (error.response.status >= 500 ? 3 : 2) : 2 // Severity: 3 for 5xx, 2 for others
        );
      } catch (trackingError) {
        // Don't let analytics errors break API error handling
        logger.warn('Failed to track error', {
          error: trackingError instanceof Error ? trackingError.message : String(trackingError),
        });
      }

      // Handle 401 Unauthorized - attempt token refresh with MSAL
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          // Force MSAL to fetch a fresh token from the server rather than returning
          // the same stale cached token that just caused the 401.  Without forceRefresh
          // MSAL may hand back the same expired token and the retry 401s again.
          const token = await msalTokenAcquisition.getAccessToken(true);

          if (token) {
            // Retry the original request with new token
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return client(originalRequest);
          } else {
            // No token available - trigger MSAL login flow
            logger.warn('401 response and no token available, triggering authentication');

            // Skip loginRedirect on /auth/callback to prevent redirect loop
            if (window.location.pathname === '/auth/callback') {
              logger.warn('On auth callback page, skipping 401 redirect');
              return Promise.reject(
                new ApiClientError('AUTH_CALLBACK', 'Auth callback in progress')
              );
            }

            const msalInstance = getMsalInstance();

            if (isAuthRedirectInProgress()) {
              return Promise.reject(
                new ApiClientError('AUTH_IN_PROGRESS', 'Authentication in progress')
              );
            }

            markAuthRedirectInProgress();

            // Save current path for redirect after login
            sessionStorage.setItem('auth_redirect_uri', window.location.pathname);

            // Clear stale MSAL cache and null out activeAccount so MSAL
            // does not auto-inject the OID-based username as login_hint
            const accounts = msalInstance.getAllAccounts();
            for (const acc of accounts) {
              msalInstance.clearCache({ account: acc });
            }
            msalInstance.setActiveAccount(null);

            // Redirect without a login hint — shows clean sign-in page
            await msalInstance.loginRedirect({ ...loginRequest });

            return Promise.reject(new ApiClientError('AUTH_REQUIRED', 'Authentication required'));
          }
        } catch (refreshError) {
          // Token acquisition failed, trigger authentication
          logger.error('Token refresh failed, triggering authentication', refreshError);

          // Skip loginRedirect on /auth/callback to prevent redirect loop
          if (window.location.pathname === '/auth/callback') {
            logger.warn('On auth callback page, skipping refresh-failure redirect');
            return Promise.reject(new ApiClientError('AUTH_CALLBACK', 'Auth callback in progress'));
          }

          const msalInstance = getMsalInstance();

          if (isAuthRedirectInProgress()) {
            return Promise.reject(
              new ApiClientError('AUTH_IN_PROGRESS', 'Authentication in progress')
            );
          }

          markAuthRedirectInProgress();

          // Save current path for redirect after login
          sessionStorage.setItem('auth_redirect_uri', window.location.pathname);

          // Clear stale MSAL cache and null out activeAccount so MSAL
          // does not auto-inject the OID-based username as login_hint
          const accounts = msalInstance.getAllAccounts();
          for (const acc of accounts) {
            msalInstance.clearCache({ account: acc });
          }
          msalInstance.setActiveAccount(null);

          // Redirect without a login hint — shows clean sign-in page
          await msalInstance.loginRedirect({ ...loginRequest });

          return Promise.reject(refreshError);
        }
      }

      // Handle 403 Forbidden - Account suspended or deleted
      if (error.response?.status === 403) {
        const responseData = error.response.data as any;
        const errorType = responseData?.errorType;

        if (errorType === 'ACCOUNT_SUSPENDED' || errorType === 'ACCOUNT_DELETED') {
          logger.warn(
            `Account ${errorType === 'ACCOUNT_SUSPENDED' ? 'suspended' : 'deleted'}, redirecting to suspension page`
          );

          // Store error message for suspended page to display
          const errorMessage =
            errorType === 'ACCOUNT_SUSPENDED'
              ? 'Your account has been suspended. Please contact support for assistance.'
              : 'Your account has been deleted.';
          sessionStorage.setItem('auth_error_message', errorMessage);

          // Redirect to account suspended page (it will handle logout when user clicks Sign Out)
          window.location.href = '/account-suspended';

          return Promise.reject(new ApiClientError(errorType, errorMessage, {}, 403));
        }
      }

      // Handle 402 Payment Required / Onboarding Required
      // This indicates the user needs to select a subscription plan.
      // IMPORTANT: We set a Redux flag and let React Router / ProtectedRoute handle
      // the redirect instead of using window.location.href.  A hard redirect restarts
      // the entire React app, which re-fires all initialization effects, which hit
      // the same 402, causing an infinite full-page-reload loop.
      if (error.response?.status === 402) {
        const responseData = error.response.data as any;

        // Check if this is an onboarding required error
        if (responseData?.details?.code === 'ONBOARDING_REQUIRED') {
          logger.info('Onboarding required, setting flag for React Router redirect');

          // Lazy-import the store to avoid circular dependencies.
          // The dynamic import is cached after first call so subsequent calls are sync.
          try {
            const { store } = await import('../store/index');
            const { setOnboardingRequired } = await import('../store/slices/authSlice');
            store.dispatch(setOnboardingRequired(true));
          } catch (storeError) {
            // Fallback: if store import fails, use hard redirect as last resort
            logger.warn('Failed to dispatch onboarding flag, falling back to redirect', {
              error: storeError instanceof Error ? storeError.message : String(storeError),
            });
            const redirectTo = getSafeSameOriginPath(
              responseData?.details?.redirectTo,
              '/onboarding/select-plan'
            );
            window.location.href = redirectTo;
          }

          return Promise.reject(
            new ApiClientError(
              'ONBOARDING_REQUIRED',
              'Please select a subscription plan',
              {
                redirectTo: responseData?.details?.redirectTo || '/onboarding/select-plan',
              },
              402
            )
          );
        }
      }

      // Transform error to ApiClientError
      const apiError = transformError(error);
      return Promise.reject(apiError);
    }
  );

  return client;
};

/**
 * Transform Axios error to ApiClientError
 */
const transformError = (error: AxiosError<ApiResponse>): ApiClientError => {
  // Network error
  if (!error.response) {
    return new ApiClientError(
      'NETWORK_ERROR',
      'Network error. Please check your internet connection.',
      { originalError: error.message }
    );
  }

  // API returned error
  const { status, data } = error.response;

  // Check if the backend returned errorType and details at the root level
  if (data && 'errorType' in data) {
    const errorData = data as {
      errorType?: string;
      error?: string;
      message?: string;
      details?: Record<string, unknown>;
    };
    return new ApiClientError(
      errorData.errorType || 'UNKNOWN_ERROR',
      errorData.error || errorData.message || 'An error occurred',
      errorData.details,
      status
    );
  }

  // Check for nested error object (legacy format)
  const apiError = data?.error;
  if (apiError && typeof apiError === 'object') {
    const nestedError = apiError as {
      code?: string;
      message?: string;
      details?: Record<string, unknown>;
    };
    return new ApiClientError(
      nestedError.code || 'UNKNOWN_ERROR',
      nestedError.message || 'An error occurred',
      nestedError.details,
      status
    );
  }

  // Fallback error
  return new ApiClientError(
    'UNKNOWN_ERROR',
    `Request failed with status ${status}`,
    { status },
    status
  );
};

/**
 * Singleton API client instance
 */
export const apiClient = createApiClient();

/**
 * Helper function to build query parameters
 */
export const buildQueryParams = (params: Record<string, unknown>): Record<string, string> => {
  const queryParams: Record<string, string> = {};

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams[key] = String(value);
    }
  });

  return queryParams;
};

/**
 * Export types for use in other services
 */
export type { ApiResponse, ApiError };
